import moment from "moment";
import {formatDate} from "utils/valueFormatter.function";
import {Assignment, Event, Step} from "types/workflowDetailsWizard";
import {StatusType} from "types/workflowDetailsWizardStep";
import {TagType} from "components/common/Tag.component";
import {NamePart, StepTag, TimelineEntry} from "types/newWorkflowDetails";

export const APPROVAL_INITIATED_KEY = "approval-initiated";
export const APPROVAL_COMPLETED_KEY = "approval-completed";

const ACTIVE_STATUSES: Array<string> = [StatusType.ACTIVE, StatusType.CURRENT];
// Same tie-break as the old history table: events at the same moment keep the workflow's order
const EVENT_TYPE_ORDER = ["START", "USER_ACTION", "END"];

export const isActiveStep = (step?: Step) => !!step && ACTIVE_STATUSES.includes(step.status);
const EXECUTED_STATUSES: Array<string> = [StatusType.PASSED, StatusType.APPROVED, StatusType.REJECTED, StatusType.CANCELED];
export const isExecutedStep = (step: Step) => EXECUTED_STATUSES.includes(step.status);
const isOpenStep = (step: Step) => isActiveStep(step) || step.status === StatusType.FUTURE;

const byDate = (first: Event, second: Event) =>
    (new Date(first.date).getTime() - new Date(second.date).getTime()) ||
    (EVENT_TYPE_ORDER.indexOf(first.eventType) - EVENT_TYPE_ORDER.indexOf(second.eventType));

/**
 * Splits the step events of the current run over its steps, so each step can list its own events.
 * Events only carry the step name, and names need not be unique, so steps are matched walking forward
 * through the workflow as the events advance; a restarted workflow (the name is only found earlier)
 * jumps back to the first step with that name. Events naming a step that is gone go to the step the
 * history last matched, as that is where the workflow stood at the time; before any step has been
 * matched that is fallbackKey (the run's workflow initiated item).
 */
export function groupEventsByStep(events: Array<Event>, steps: Array<Step>, fallbackKey: string): Map<string, Array<Event>> {
    const groups = new Map<string, Array<Event>>();
    const add = (key: string, event: Event) => groups.set(key, [...(groups.get(key) ?? []), event]);

    let lastMatchedKey = fallbackKey;
    const findStep = (name: string, from: number) => {
        const ahead = steps.findIndex((step, index) => index >= from && step.name === name);
        return ahead !== -1 ? ahead : steps.findIndex((step) => step.name === name);
    }

    let currentStepIndex = 0;
    [...events].sort(byDate).forEach((event) => {
        const index = findStep(event.stepName, currentStepIndex);
        if (index === -1) {
            add(lastMatchedKey, event);
            return;
        }
        currentStepIndex = index;
        lastMatchedKey = stepKey(steps[index]);
        add(lastMatchedKey, event);
    });

    return groups;
}

/**
 * Lays the workflow history out in the order it happened. A task can go through approval more than once
 * (initiated, completed, initiated again), so every START opens a run with its own "Workflow initiated"
 * item and every END closes it with a "Workflow completed" item, like the rows of the old history table.
 * Only the latest run has its steps in the workflow details; the step events of earlier runs are listed
 * under the step names they carry, one item per stretch of events in the same step. When the latest run
 * ended while some of its steps were still open (not started or in process), its completed item goes
 * before those steps, as the workflow never got to them.
 */
export function buildTimeline(events: Array<Event>, steps: Array<Step>): Array<TimelineEntry> {
    const runs: Array<Array<Event>> = [[]];
    [...events].sort(byDate).forEach((event) => {
        if (event.eventType === "START" && runs[runs.length - 1].length > 0) runs.push([]);
        runs[runs.length - 1].push(event);
    });

    const timeline: Array<TimelineEntry> = [];
    runs.forEach((run, runIndex) => {
        const isLatestRun = runIndex === runs.length - 1;
        const initiatedKey = `${APPROVAL_INITIATED_KEY}-${runIndex}`;
        const endIndex = run.findIndex((event) => event.eventType === "END");
        // Anything logged after the END (before the next START) is part of how the run was completed
        const completedEvents = endIndex === -1 ? [] : run.slice(endIndex);
        const runEvents = endIndex === -1 ? run : run.slice(0, endIndex);
        const startEvents = runEvents.filter((event) => event.eventType === "START");
        const stepEvents = runEvents.filter((event) => event.eventType !== "START");

        if (isLatestRun) {
            // Events from before any current step was reached are listed with the start of the run
            const eventsByStep = groupEventsByStep(stepEvents, steps, initiatedKey);
            const beforeSteps = eventsByStep.get(initiatedKey) ?? [];
            eventsByStep.delete(initiatedKey);
            timeline.push({kind: "initiated", key: initiatedKey, events: [...startEvents, ...beforeSteps]});

            const firstOpenIndex = completedEvents.length > 0 ? steps.findIndex(isOpenStep) : -1;
            if (firstOpenIndex === -1) {
                timeline.push({kind: "steps", key: `steps-${runIndex}`, steps, eventsByStep});
                pushCompleted(timeline, runIndex, completedEvents);
            } else {
                if (firstOpenIndex > 0) {
                    timeline.push({kind: "steps", key: `steps-${runIndex}`, steps: steps.slice(0, firstOpenIndex), eventsByStep});
                }
                pushCompleted(timeline, runIndex, completedEvents);
                timeline.push({kind: "steps", key: `open-steps-${runIndex}`, steps: steps.slice(firstOpenIndex), eventsByStep});
            }
        } else {
            timeline.push({kind: "initiated", key: initiatedKey, events: startEvents});
            pastRunSteps(stepEvents).forEach((pastStep, index) =>
                timeline.push({kind: "pastStep", key: `run-${runIndex}-step-${index}`, ...pastStep}));
            pushCompleted(timeline, runIndex, completedEvents);
        }
    });

    return timeline;
}

function pushCompleted(timeline: Array<TimelineEntry>, runIndex: number, completedEvents: Array<Event>) {
    if (completedEvents.length > 0) {
        timeline.push({kind: "completed", key: `${APPROVAL_COMPLETED_KEY}-${runIndex}`, events: completedEvents});
    }
}

// Consecutive events of the same step make one item; an event without a step stays with the one before it
function pastRunSteps(events: Array<Event>): Array<{ name: string, events: Array<Event> }> {
    const pastSteps: Array<{ name: string, events: Array<Event> }> = [];
    events.forEach((event) => {
        const last = pastSteps[pastSteps.length - 1];
        if (last && (!event.stepName || event.stepName === last.name)) {
            last.events.push(event);
        } else {
            pastSteps.push({name: event.stepName ?? "", events: [event]});
        }
    });
    return pastSteps;
}

/** The status an action ended a step or the workflow in: the END event of a run, or the last event of a past step */
export function statusFromAction(event?: Event): StatusType | null {
    switch (event?.action) {
        case "APPROVED":
            return StatusType.APPROVED;
        case "REJECTED":
            return StatusType.REJECTED;
        case "CANCELED":
            return StatusType.CANCELED;
        default:
            return null;
    }
}

export const stepKey = (step: Step) => `step-${step.stepNumber}`;

/** When an executed step was acted on: its lastAction, or else the date of the last event logged in it */
export function executedDate(step: Step, events: Array<Event>): Date | string | undefined {
    if (!isExecutedStep(step)) return undefined;
    return step.lastAction || [...events].sort(byDate)[events.length - 1]?.date;
}

/** The date and the time of day, as the workflow history shows when something happened */
export const formatDateTime = (date: Date | string) => `${formatDate(date, null)} ${moment(date).format("HH:mm")}`;

/** "A", "A and B", "A, B and C", "A, B and 3 more", split into the names and the words joining them */
export function joinNameParts(names: Array<string>, and: string, more: string, maxNames = 3): Array<NamePart> {
    // Each part is keyed by what it shows: a name by the name (and which occurrence of it, should a user be
    // listed twice), the words between names by the part they come before
    const occurrences = new Map<string, number>();
    const namePart = (name: string): NamePart => {
        const occurrence = (occurrences.get(name) ?? 0) + 1;
        occurrences.set(name, occurrence);
        return {key: `name:${name}:${occurrence}`, text: name, isName: true};
    }
    const separator = (text: string, before: NamePart): NamePart => ({key: `before:${before.key}`, text, isName: false});

    if (names.length <= 1) return names.length ? [namePart(names[0])] : [];

    const shown = (names.length > maxNames ? names.slice(0, maxNames - 1) : names.slice(0, -1)).map(namePart);
    const last: NamePart = names.length > maxNames ?
        {key: "more", text: `${names.length - shown.length} ${more}`, isName: false} :
        namePart(names[names.length - 1]);

    const parts: Array<NamePart> = [];
    shown.forEach((part) => {
        if (parts.length > 0) parts.push(separator(", ", part));
        parts.push(part);
    });
    parts.push(separator(` ${and} `, last), last);
    return parts;
}

export const assignmentNames = (assignments: Array<Assignment> = []) =>
    assignments.map((assignment) => `${assignment.userDTO.firstName} ${assignment.userDTO.lastName}`);

/** The backend sends the reason as "Reason: <newest>\n<older>", newest line last */
export function assignmentReasons(assignment: Assignment): Array<string> {
    return (assignment.explanation ?? "")
        .replace("Reason: ", "")
        .split("\n")
        .filter((part) => part.trim() !== "")
        .reverse();
}

/**
 * A mandatory step the workflow found no approvers for: the backend explains why in errorExplanations and
 * the step has no assignments at all, so it cannot be approved until the workflow configuration is fixed.
 * Only a step the workflow has yet to get past can be blocked like that; a skipped or executed step with
 * errors keeps its status and shows the backend's explanation.
 */
export const isConfigurationErrorStep = (step: Step) =>
    isOpenStep(step) &&
    (step.errorExplanations?.length ?? 0) > 0 &&
    (step.completedAssignments?.length ?? 0) === 0 &&
    (step.incompleteAssignments?.length ?? 0) === 0;

export const CONFIGURATION_ERROR_TAG: StepTag = {type: TagType.ERROR, translationKey: "taskDetail.stepStatus.CONFIGURATION_ERROR"};

export function stepTag(status: StatusType): StepTag | null {
    switch (status) {
        case StatusType.PASSED:
        case StatusType.APPROVED:
            return {type: TagType.SUCCESS, translationKey: "taskDetail.stepStatus.APPROVED"};
        case StatusType.ACTIVE:
        case StatusType.CURRENT:
            return {type: TagType.INFORMATION, translationKey: "taskDetail.stepStatus.IN_PROCESS"};
        case StatusType.FUTURE:
            return {type: TagType.DISABLED, translationKey: "taskDetail.stepStatus.NOT_STARTED"};
        case StatusType.SKIPPED:
            return {type: TagType.DISABLED, translationKey: "taskDetail.stepStatus.SKIPPED"};
        case StatusType.REJECTED:
            return {type: TagType.ERROR, translationKey: "taskDetail.stepStatus.REJECTED"};
        case StatusType.CANCELED:
            return {type: TagType.WARNING, translationKey: "taskDetail.stepStatus.CANCELED"};
        default:
            return null;
    }
}
